<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useFocusTrap } from '../../../composables/useFocusTrap'
import { useReturnFocus } from '../../../composables/useReturnFocus'
import { useAnchoredPosition, type AnchorPlacement } from './use-anchored-position'
import AppScrollArea from '../AppScrollArea/AppScrollArea.vue'

const open = defineModel<boolean>('open', { default: false })

const props = withDefaults(
  defineProps<{
    placement?: AnchorPlacement
    offset?: number
    matchTriggerWidth?: boolean
    /** ARIA role for the panel. Menus override this to `menu`. */
    role?: string
    /** `aria-haspopup` value surfaced to the trigger via slot props. */
    ariaHaspopup?: 'dialog' | 'menu' | 'listbox' | 'grid' | 'tree' | 'true'
    /** Trap Tab within the panel while open. Off for menus (roving focus). */
    trapFocus?: boolean
    autoFocus?: boolean
  }>(),
  {
    placement: 'bottom-start',
    offset: 8,
    matchTriggerWidth: false,
    role: 'dialog',
    ariaHaspopup: 'dialog',
    trapFocus: true,
    autoFocus: true,
  },
)

const emit = defineEmits<{
  opened: []
  closed: []
}>()

const triggerRef = ref<HTMLElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)

const trap = useFocusTrap(panelRef)
const returnFocus = useReturnFocus()

const { floatingStyles, start, stop } = useAnchoredPosition(triggerRef, panelRef, () => ({
  placement: props.placement,
  offset: props.offset,
  matchWidth: props.matchTriggerWidth,
}))

let skipReturnFocus = false

function toggle() {
  open.value = !open.value
}

function close() {
  open.value = false
}

function onDocumentPointerDown(event: PointerEvent) {
  const target = event.target as Node | null
  if (!target) return
  if (panelRef.value?.contains(target)) return
  if (triggerRef.value?.contains(target)) return
  skipReturnFocus = true
  close()
}

function onKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape' || !open.value) return
  event.preventDefault()
  close()
}

async function handleOpen() {
  if (typeof document === 'undefined') return
  returnFocus.capture()
  document.addEventListener('keydown', onKeydown)
  document.addEventListener('pointerdown', onDocumentPointerDown, true)
  await nextTick()
  start()
  if (props.trapFocus) trap.activate()
  if (props.autoFocus) trap.focusFirstTabbable()
  emit('opened')
}

function handleClose() {
  if (typeof document === 'undefined') return
  document.removeEventListener('keydown', onKeydown)
  document.removeEventListener('pointerdown', onDocumentPointerDown, true)
  stop()
  if (props.trapFocus) trap.deactivate()
  if (!skipReturnFocus) returnFocus.restore()
  skipReturnFocus = false
  emit('closed')
}

watch(open, (isOpen) => {
  if (isOpen) handleOpen()
  else handleClose()
})

onMounted(() => {
  if (open.value) handleOpen()
})

onBeforeUnmount(() => {
  if (open.value) handleClose()
})

defineExpose({ close })
</script>

<template>
  <span ref="triggerRef" class="contents" @click="toggle">
    <slot
      name="trigger"
      :open="open"
      :aria="{ 'aria-haspopup': ariaHaspopup, 'aria-expanded': open }"
    />
  </span>

  <Teleport to="body">
    <Transition
      enter-active-class="popover-enter-active"
      enter-from-class="popover-enter-from"
      leave-active-class="transition-[opacity,transform] duration-fast ease-soft-exit motion-reduce:transition-none"
      leave-to-class="opacity-0 translate-y-1"
    >
      <div
        v-if="open"
        ref="panelRef"
        :role="role"
        tabindex="-1"
        :style="floatingStyles"
        class="glass-island z-50 flex min-w-64 flex-col overflow-hidden rounded-4 outline-none"
      >
        <AppScrollArea class="min-h-0">
          <slot />
        </AppScrollArea>
      </div>
    </Transition>
  </Teleport>
</template>
